import {
  type BasicCouponFragment,
  type BasicServiceFragment,
  type CompleteStoreFragment,
  type Maybe,
} from '@drycut/graphql';
import { type DateTime } from 'luxon';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

type CheckoutState = {
  manual: boolean;
  phoneValidated: boolean;
  preSelectedStore?: Maybe<CompleteStoreFragment>;
  scheduled: boolean;
  selectedCoupon?: BasicCouponFragment;
  selectedSchedule?: DateTime;
  selectedServices: Array<BasicServiceFragment>;
  selectedStore?: Maybe<CompleteStoreFragment>;
};

type CheckoutActions = {
  setPhoneValidated: (validated: boolean) => void;
  setSelectedCoupon: (coupon?: BasicCouponFragment) => void;
  setSelectedSchedule: (date?: DateTime) => void;
  setSelectedServices: (services: Array<BasicServiceFragment>) => void;
  setSelectedStore: (store: CompleteStoreFragment) => void;
  toggleScheduled: () => void;
};

export const useCheckoutStore = create<CheckoutState & CheckoutActions>()(
  devtools((set): CheckoutState & CheckoutActions => {
    return {
      // State
      manual: false,
      selectedStore: undefined,
      scheduled: false,
      selectedSchedule: undefined,
      selectedCoupon: undefined,
      selectedServices: [],
      phoneValidated: false,
      preSelectedStore: undefined,

      setSelectedStore: (store) => {
        set({ selectedStore: store }, undefined, 'setSelectedStore');
      },

      toggleScheduled: () => {
        // Using the value from useBoolean hook
        set((state) => ({ scheduled: !state.scheduled }), undefined, 'toggleScheduled');
      },

      setSelectedSchedule: (date) => {
        set({ selectedSchedule: date, scheduled: Boolean(date) }, undefined, 'setSelectedSchedule');
      },

      setSelectedCoupon: (coupon) => {
        set({ selectedCoupon: coupon }, undefined, 'setSelectedCoupon');
      },

      setSelectedServices: (services: Array<BasicServiceFragment>) => {
        set({ selectedServices: services }, undefined, 'setSelectedServices');
      },

      // Result of the async phone number validation. The customer step owns when this is set, the
      // BookingCheckout component derives the step validity from it.
      setPhoneValidated: (validated: boolean) => {
        set({ phoneValidated: validated }, undefined, 'setPhoneValidated');
      },
    };
  }),
);
